import type { Extras, Primitive, Scope } from '@sentry/core'

/**
 * Sentry tag keys for categorizing events
 */
export enum SentryTags {
  ACTION_TYPE = 'action_type',
  SOURCE = 'source',
}

/**
 * Interface for error context information to be passed to Sentry
 */
export interface ErrorContext extends Extras {
  source?: string
  requestMethod?: string
  requestUrl?: string
  requestErrorMessage?: string
  statusCode?: number
  errorType?: string
  type?: string
}

/**
 * Status codes that should not be logged to Sentry
 */
export const STATUS_CODES_TO_NOT_LOG = [401, 403]

/**
 * Function to replace the UUID such as lyraId or episodeId
 * in a requestURL. This is so that sentry does not create multiple unique
 * events for each unique UUID.
 */
export function replaceUUID(path: string) {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
  const pathSegments = path.split('/')

  for (const segment of pathSegments) {
    if (uuidRegex.test(segment)) {
      path = path.replace(segment, 'someUUID')
    }
  }
  return path
}

/**
 * Generic interface for Sentry SDK
 * This allows us to use the same function with different Sentry SDKs
 */
export interface SentrySDK {
  setTag: (key: string, value: Primitive) => void
  withScope: (callback: (scope: Scope) => void) => void
  captureException: (error: Error) => string
}

/**
 * Shared implementation of sentryCaptureException that works with both web and mobile SDKs
 *
 * NOTE: ONLY use for native exceptions OR for very rare cases, not javascript exceptions
 * Use dispatch(log({})) for javascript exceptions
 * We want to ONLY call to sentry when there are exceptions that may affect the app natively,
 * such as when there are missing tokens not built with the app.
 *
 * Explicitly logging to Sentry for errors or debug related messages that are not app crashes can bloat the Sentry Dashboard
 * making it hard for us to tackle errors in a meaningful way
 *
 * @param sentry The Sentry SDK instance to use
 * @param err The error to capture
 * @param extras Additional context for the error
 * @returns The Sentry event ID
 */
export const sentryCaptureExceptionShared = (sentry: SentrySDK, err: Error, extras: ErrorContext): string => {
  let sentryID = 'No Sentry Event ID'
  try {
    if (extras?.type) {
      // helps us categorize the redux actions in sentry as tags can be filtered out
      sentry.setTag(SentryTags.ACTION_TYPE, extras.type as Primitive)
    }
    if (extras?.source) {
      // helps us categorize the redux actions in sentry as tags can be filtered out
      sentry.setTag(SentryTags.SOURCE, extras.source as Primitive)
    }
    sentry.withScope((scope) => {
      scope.setExtras(extras)
      if (extras?.requestUrl) {
        scope.setFingerprint([
          extras?.requestMethod ?? '',
          replaceUUID(extras?.requestUrl ?? ''),
          String(extras?.statusCode),
        ])
        scope.setTags({
          endpoint_url: extras?.requestUrl,
          endpoint_method: extras?.requestMethod,
          http_error_code: extras?.statusCode,
          api_error_message: extras?.requestErrorMessage,
        })
      }
      sentryID = sentry.captureException(err)
    })
    console.error(err.message, extras)
    return sentryID
  } catch (error) {
    console.error(`Unable to log to sentry for error: ${error}`)
    return sentryID
  }
}
